import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "@/features/utils/i18n";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { RRulesEditor } from "@/views/backoffice/schedules/components/schedules/rrules-editor";

const DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;
type Day = (typeof DAYS)[number];

type Slot = { from: string; to: string };

/**
 * Read a schedule blob back into per-day slots, for display only.
 *
 * Deliberately a local, lossy read: exception lines are ignored because this is
 * a summary, and the editor below is the thing that round-trips them.
 */
const parse = (rrules: string): Record<Day, Slot[]> => {
  const week = Object.fromEntries(
    DAYS.map((day) => [day, [] as Slot[]]),
  ) as Record<Day, Slot[]>;

  for (const line of (rrules || "").split("\n")) {
    const match =
      /^RRULE:FREQ=WEEKLY;BYDAY=([A-Z,]+);BYHOUR=(\d{1,2});BYMINUTE=(\d{1,2});DURATION=(\d+)$/.exec(
        line.trim(),
      );
    if (!match) continue;

    const [, days, hour, minute, duration] = match;
    const start = Number(hour) * 60 + Number(minute);
    const end = start + Number(duration);
    const format = (minutes: number) =>
      `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(
        minutes % 60,
      ).padStart(2, "0")}`;

    for (const day of days.split(",")) {
      if (DAYS.includes(day as Day)) {
        week[day as Day].push({ from: format(start), to: format(end) });
      }
    }
  }

  for (const day of DAYS) {
    week[day].sort((a, b) => (a.from < b.from ? -1 : 1));
  }
  return week;
};

const sameHours = (a: Slot[], b: Slot[]) =>
  a.length === b.length &&
  a.every(
    (slot, index) => slot.from === b[index].from && slot.to === b[index].to,
  );

/**
 * Opening hours as a sentence, not a grid.
 *
 * A week of dropdowns is the right tool for *editing* and the wrong one for
 * *confirming* — and confirming is what this step is for. Consecutive days that
 * share their hours collapse into a range ("Mardi – Vendredi"), so the usual
 * business reads as two lines the client can check at a glance and move on.
 * The full editor is one click away, in a dialog, for the minority who need it.
 */
export function ScheduleSummary({
  rrules,
  onChange,
}: {
  rrules: string;
  onChange: (rrules: string) => void;
}) {
  const { t } = useTranslation("onboarding");
  const { t: tSchedules } = useTranslation("schedules");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(rrules);

  const week = parse(rrules);

  const label = (day: Day) =>
    tSchedules(
      `days.${
        {
          MO: "monday",
          TU: "tuesday",
          WE: "wednesday",
          TH: "thursday",
          FR: "friday",
          SA: "saturday",
          SU: "sunday",
        }[day]
      }`,
    );

  // Collapse consecutive days with identical hours into one row.
  const rows: { days: Day[]; slots: Slot[] }[] = [];
  for (const day of DAYS) {
    const previous = rows[rows.length - 1];
    if (previous && sameHours(previous.slots, week[day])) {
      previous.days.push(day);
    } else {
      rows.push({ days: [day], slots: week[day] });
    }
  }

  return (
    <div className="space-y-3">
      <div className="divide-y rounded-lg border">
        {rows.map(({ days, slots }) => (
          <div
            key={days.join("-")}
            className="flex items-baseline justify-between gap-4 px-4 py-2.5 text-sm"
          >
            <span className="font-medium">
              {days.length === 1
                ? label(days[0])
                : `${label(days[0])} – ${label(days[days.length - 1])}`}
            </span>
            <span
              className={
                slots.length === 0 ? "text-muted-foreground" : "tabular-nums"
              }
            >
              {slots.length === 0
                ? t("steps.hours.closed")
                : slots.map((slot) => `${slot.from} – ${slot.to}`).join(" · ")}
            </span>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          setEditing(rrules);
          setOpen(true);
        }}
      >
        <PencilSimpleIcon className="h-4 w-4" />
        {t("steps.hours.adjust")}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("steps.hours.adjustTitle")}</DialogTitle>
          </DialogHeader>

          {/* The back-office editor, exceptions hidden — same component the
              client will meet again on the schedules page. */}
          <RRulesEditor
            value={editing}
            onChange={setEditing}
            showExceptions={false}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("actions.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                onChange(editing);
                setOpen(false);
              }}
            >
              {t("actions.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
